import React, { useEffect, useMemo, useState } from 'react';
import CardContainer from 'components/Tvendo/Common/CardContainer';
import RegularButton from 'components/CustomButtons/Button';
import ReactTable from 'components/ReactTable/ReactTable';
import { Checkbox, TextField } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';
import { wsGetAll, wsCreate } from 'services/WService';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import ProcessNCModal from './ProcessNCModal';

const columns = [
  { Header: '#', accessor: 'num', excelEnabled: false, width: 85 },
  { Header: 'Validación', accessor: 'validation', excelEnabled: false, width: 100 },
  { Header: 'Cod.Socio', accessor: 'CardCode', width: 120, excelEnabled: false, align: 'center' },
  { Header: 'Socio', accessor: 'CardName', width: 400, excelEnabled: false },
  { Header: 'Fecha Doc.', accessor: 'TaxDate', width: 100, excelEnabled: false },

  { Header: 'T.Doc Sun.', accessor: 'TipoDoc', width: 100, excelEnabled: false },
  { Header: 'Serie Doc.', accessor: 'SerieDoc', width: 100, excelEnabled: false },
  { Header: 'Corr. Sun.', accessor: 'NumeroDoc', width: 110, excelEnabled: false },

  // La NC del documento (si tiene varias, vienen separadas por coma)
  { Header: 'T.Doc NC', accessor: 'TipoNC', width: 100, excelEnabled: false },
  { Header: 'Serie NC', accessor: 'SerieNC', width: 100, excelEnabled: false },
  { Header: 'Corr. NC', accessor: 'NumeroNC', width: 110, excelEnabled: false },

  { Header: 'Huerfanos', accessor: 'TieneNCHuerfanos', width: 120, excelEnabled: false },

  { Header: 'Saldo Doc', accessor: 'SaldoOINV', width: 120, excelEnabled: false },
  { Header: 'Saldo NC', accessor: 'SaldoORIN', width: 120, excelEnabled: false },
  { Header: 'Direrencia', accessor: 'Diferencia', width: 120, excelEnabled: false },

  { Header: '', accessor: 'actions', excelEnabled: false, width: 1 },
];

function ApplicateNCList() {
  const [documents, setDocuments] = useState([]);
  const navigate = useNavigate();
  const [dataLoading, setDataLoading] = useState(false);
  const [validating, setValidating] = useState('');
  const [processOpen, setProcessOpen] = useState({ open: false });
  // Fecha del documento. Sin "Desde" trae todo lo pendiente hasta "Hasta".
  const [filter, setFilter] = useState({
    start_date: '',
    end_date: moment().format('YYYY-MM-DD'),
  });

  // Se procesa lo validado. Los huérfanos no se pueden procesar.
  const validados = useMemo(
    () => documents.filter((doc) => doc.validation_sap === 'Y' && doc.TieneNCHuerfanos !== 'Si'),
    [documents],
  );

  async function listDocuments() {
    setDataLoading(true);
    let res = await wsGetAll(
      `pymt/received-payment-cn/customers?start_date=${filter.start_date}&end_date=${filter.end_date}`,
    );
    setDataLoading(false);
    if (res.success) {
      setDocuments(res.data);
    }
  }

  /** El flag vive en cada documento: se marca solo el de la fila. */
  async function toggleValidation(doc, validar) {
    let fecha = moment(doc.TaxDate).format('YYYY-MM-DD');
    setValidating(doc.DocEntry);
    let res = await wsCreate('pymt/received-payment-cn/validation', {
      CardCode: doc.CardCode,
      zone_code: doc.SeriesStr,
      start_date: fecha,
      end_date: fecha,
      DocEntry: doc.DocEntry,
      validation: validar ? 'Y' : 'N',
    });
    setValidating('');

    if (!res.success) {
      alert(res.message);
      return;
    }

    setDocuments((prev) =>
      prev.map((item) =>
        item.DocEntry === doc.DocEntry ? { ...item, validation_sap: validar ? 'Y' : null } : item,
      ),
    );
  }

  function navigateDetail(doc) {
    let fecha = moment(doc.TaxDate).format('YYYY-MM-DD');
    navigate(`/admin/bancos/operaciones/aplicar-nc/${doc.CardCode}?start_date=${fecha}&end_date=${fecha}`);
  }

  useEffect(() => {
    listDocuments();
  }, []);

  return (
    <CardContainer
      title={
        <>
          NC Oficina pendientes de aplicar{dataLoading && <LoadingSpinner />}
          <RegularButton
            color="success"
            size="sm"
            style={{ float: 'right' }}
            disabled={validados.length === 0}
            onClick={() => setProcessOpen({ open: true })}
          >
            Procesar ({validados.length})
          </RegularButton>
        </>
      }
    >
      <ProcessNCModal
        model={processOpen}
        setOpen={setProcessOpen}
        rows={validados}
        reload={listDocuments}
      />
      <GridContainer>
        <GridItem xs={12} sm={3}>
          <TextField
            label="Desde"
            type="date"
            fullWidth
            value={filter.start_date}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: filter.end_date }}
            onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
          />
        </GridItem>
        <GridItem xs={12} sm={3}>
          <TextField
            label="Hasta"
            type="date"
            fullWidth
            value={filter.end_date}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: filter.start_date }}
            onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
          />
        </GridItem>
        <GridItem xs={12} sm={2}>
          <RegularButton color="primary" size="sm" disabled={dataLoading} onClick={listDocuments}>
            Buscar
          </RegularButton>
        </GridItem>
      </GridContainer>
      <ReactTable
        columns={columns}
        formatDate="DD-MM-YYYY"
        data={documents.map((doc, index) => {
          let huerfano = doc.TieneNCHuerfanos === 'Si';

          return {
            num: (
              <div className="actions-right">
                {index + 1}
                <RegularButton
                  justIcon
                  round
                  simple
                  color="primary"
                  onClick={() => navigateDetail(doc)}
                >
                  <Visibility />
                </RegularButton>
              </div>
            ),
            validation: (
              <Checkbox
                color="primary"
                disabled={huerfano || validating === doc.DocEntry}
                checked={doc.validation_sap === 'Y'}
                onChange={(e) => toggleValidation(doc, e.target.checked)}
              />
            ),
            ...doc,
            SaldoOINV: parseFloat(doc.SaldoOINV).toFixed(2),
            SaldoORIN: parseFloat(doc.SaldoORIN).toFixed(2),
            Diferencia: parseFloat(doc.Diferencia).toFixed(2),
          };
        })}
      />
    </CardContainer>
  );
}

export default ApplicateNCList;
